import { SportsEnum } from 'types/generated/client';
import type { FindFilters } from 'screens/Find/filters';
import { DEFAULT_SEARCH_CENTER_NAME } from 'screens/Find/geo';
import Button from 'components/Button';
import { getSports } from 'components/Explore/Lessons/utils';
import { NeighborhoodSelect, useNeighborhoods } from 'components/Find/Neighborhoods';
import Dropdown from 'components/SearchBar/Dropdown';
import classNames from 'styles/utils/classNames';
import { getDateRangeOptions, getIntentOptions } from './options';
import { HomeDateRangeEnum, HomeFilters, HomeIntentEnum } from './types';

interface Props {
  filters: HomeFilters;
  onChange: (filters: HomeFilters) => void;
  /** The feed sheet's filters: the desktop location pill edits their neighborhoods. */
  sheetFilters: FindFilters;
  onSheetFiltersChange: (filters: FindFilters) => void;
}

// The mobile pill carries the shadow for both dropdowns, so the triggers only get one at lg.
const PILL_SHADOW_DESKTOP_CLASS = 'lg:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]';
// Below lg the two dropdowns share one white pill, so the individual triggers drop
// their own border/shadow/height and only keep a divider between them. `!shadow-none`
// also suppresses the global `button` focus ring, which is drawn as a box-shadow and
// would otherwise trace half the pill (desktop masks it the same way, via its own shadow).
const PILL_BUTTON_CLASS =
  'max-lg:h-12 max-lg:border-0 max-lg:px-ds-md max-lg:!shadow-none lg:h-14 lg:border lg:border-color-border-input-lightmode lg:px-5 lg:dark:border-color-border-input-darkmode';
const PILL_OPEN_CLASS =
  'lg:border-color-border-hover-lightmode lg:!shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] lg:dark:border-color-border-hover-darkmode';

// The mobile panel spans the whole pill (both halves), so it is twice its trigger's width.
const PANEL_MOBILE_CLASS =
  'max-lg:mt-ds-md max-lg:w-[200%] max-lg:shadow-[0px_12px_40px_0px_rgba(0,0,0,0.3)]';
// Taller rows + full-size icons in the panel, which the trigger shrinks to 20px on mobile.
const OPTION_MOBILE_CLASS = 'max-lg:h-12 max-lg:[&_img]:h-6 max-lg:[&_img]:w-6';

const dropdownSharedProps = {
  shadowClassName: PILL_SHADOW_DESKTOP_CLASS,
  openButtonClassName: PILL_OPEN_CLASS,
  overlayClassName: 'flex flex-col',
  optionClassName: OPTION_MOBILE_CLASS,
  highlightSelected: true,
  chevronClassName: 'h-5 w-5',
  labelClassName: 'typography-product-subheading-mobile lg:typography-product-subheading',
};

export default function GetOnCourtSection({
  filters,
  onChange,
  sheetFilters,
  onSheetFiltersChange,
}: Props) {
  const { neighborhoods } = useNeighborhoods();
  const separatorClass =
    'typography-product-body text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder max-lg:hidden';

  return (
    // backdrop-blur below makes the bar a stacking context, so the dropdown panels need
    // the whole section raised above later sections.
    <section className="container relative z-20 mx-auto">
      <div
        className={classNames(
          'flex w-full items-center gap-ds-lg max-lg:flex-col',
          // The card chrome is desktop-only — on mobile the bar sits bare on the page.
          'lg:flex-nowrap lg:rounded-full lg:border lg:border-color-border-input-lightmode lg:bg-[rgba(255,255,255,0.9)] lg:px-ds-2xl lg:py-ds-xl lg:backdrop-blur-[5px] lg:dark:border-color-border-input-darkmode',
          'lg:shadow-[0px_30px_100px_0px_rgba(53,62,88,0.16)]',
        )}
      >
        <h1 className="typography-product-display whitespace-nowrap px-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
          Get on court
        </h1>
        {/* Date range is a dropdown on desktop and a row of chips on mobile (below). */}
        <Dropdown
          options={getDateRangeOptions()}
          selectedValue={filters.dateRange}
          onSelect={(value) => onChange({ ...filters, dateRange: value as HomeDateRangeEnum })}
          wrapperClassName="max-lg:hidden lg:w-[12.5rem]"
          {...dropdownSharedProps}
          buttonClassName={PILL_BUTTON_CLASS}
          gapClassName="gap-[0.4375rem]"
        />
        {/* `lg:contents` dissolves the mobile pill so the desktop bar keeps its flat row. */}
        <div className="flex w-full items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:bg-color-bg-darkmode-primary lg:contents">
          <Dropdown
            options={getSports()}
            selectedValue={filters.sport}
            onSelect={(value) => onChange({ ...filters, sport: value as SportsEnum })}
            wrapperClassName="max-lg:min-w-0 max-lg:flex-1 lg:w-[11.25rem]"
            {...dropdownSharedProps}
            overlayClassName={classNames('flex flex-col', PANEL_MOBILE_CLASS)}
            buttonClassName={classNames(
              PILL_BUTTON_CLASS,
              'max-lg:rounded-r-none max-lg:border-r max-lg:border-color-border-input-lightmode max-lg:dark:border-color-border-input-darkmode',
            )}
          />
          <span className={separatorClass}>to</span>
          <Dropdown
            options={getIntentOptions()}
            selectedValue={filters.intent}
            onSelect={(value) => onChange({ ...filters, intent: value as HomeIntentEnum })}
            wrapperClassName="max-lg:min-w-0 max-lg:flex-1 lg:w-[12.5rem]"
            {...dropdownSharedProps}
            overlayClassName={classNames('flex flex-col', PANEL_MOBILE_CLASS, 'max-lg:right-0')}
            buttonClassName={classNames(PILL_BUTTON_CLASS, 'max-lg:rounded-l-none')}
          />
        </div>
        <div
          className={classNames(
            'w-full flex-wrap items-start justify-center gap-ds-md max-xs:justify-between lg:hidden',
            // Book a court shows the day strip in the feed instead of these chips.
            filters.intent === HomeIntentEnum.BookCourt ? 'hidden' : 'flex',
          )}
        >
          {getDateRangeOptions('h-5 w-5').map((option) => {
            const isSelected = option.value === filters.dateRange;

            return (
              <Button
                key={option.value}
                variant="secondary"
                size="sm"
                isInline
                iconLeft={option.icon}
                className={classNames(
                  // `!bg-*` pins the fill in every state: the shared hover fill would
                  // otherwise make an unselected chip read as selected on tap-and-hold.
                  'h-9 whitespace-nowrap !px-ds-md font-bold',
                  isSelected
                    ? '!border-transparent !bg-color-bg-lightmode-secondary shadow-[inset_0px_1px_2px_0px_rgba(0,0,0,0.16)] dark:!bg-color-bg-darkmode-secondary'
                    : '!border-color-border-input-lightmode !bg-color-bg-lightmode-primary dark:!border-color-border-input-darkmode dark:!bg-color-bg-darkmode-primary',
                )}
                onClick={() => onChange({ ...filters, dateRange: option.value })}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
        <span className={separatorClass}>in</span>
        <NeighborhoodSelect
          variant="pill"
          className="min-w-0 flex-1 max-lg:hidden"
          buttonClassName={classNames(PILL_BUTTON_CLASS, PILL_SHADOW_DESKTOP_CLASS)}
          openButtonClassName={PILL_OPEN_CLASS}
          options={neighborhoods}
          value={sheetFilters.neighborhoods}
          onChange={(rows) => onSheetFiltersChange({ ...sheetFilters, neighborhoods: rows })}
          // The sheet's venue whitelist still applies when no neighborhood is picked.
          placeholder={
            sheetFilters.venues.length > 0 ? 'At your selected venues' : DEFAULT_SEARCH_CENTER_NAME
          }
        />
      </div>
    </section>
  );
}
